export type RatingFactors = {
  quality?: number;      // جودة المنتج/الخدمة
  service?: number;      // خدمة العملاء
  price?: number;        // السعر/القيمة مقابل المال
  delivery?: number;     // التوصيل (for e-commerce)
  atmosphere?: number;   // الأجواء (for restaurants/cafes)
  cleanliness?: number;  // النظافة (for restaurants/services)
  variety?: number;      // التنوع (for products/menu)
  location?: number;     // الموقع
  packaging?: number;    // التغليف (for products)
  experience?: number;   // التجربة العامة
};

export type ReviewComment = {
  _id: string;
  user: {
    _id: string;
    full_name: string;
    avatar_url?: string;
  };
  text: string;
  // MERCH-07/D-10: true when this comment is the store owner's public reply.
  isMerchantReply?: boolean;
  createdAt: string;
};

export type Review = {
  _id: string;
  user: {
    _id: string;
    full_name: string;
    avatar_url?: string;
    // The author's contribution TITLE. Absent on imported reviews, where no account stands
    // behind the name. The raw points number is deliberately not in this payload at all: a card
    // that cannot read it cannot render it (spec §9).
    tier?: { level: number; ar: string; en: string };
  } | null;
  text?: string;
  overallRating: number;
  ratingFactors: RatingFactors;
  likesCount: number;
  isLikedByCurrentUser: boolean;
  commentsCount: number;
  comments: ReviewComment[];
  isAnonymous: boolean;
  isVerifiedPurchase: boolean;
  // Phase 48 D-24/D-26: order-backed verification. The public badge stays «عميل موثق»; a
  // shopify_order review with refundedAt also shows a neutral «تم الاسترداد» tag.
  purchaseVerification?: { method: string; refundedAt?: string };
  isExternal: boolean;
  externalSource?: string;
  externalAuthor?: string;
  externalDate?: string;
  sourceData?: {
    postUrl?: string;
    reviewUrl?: string;
    groupName?: string;
    groupUrl?: string;
    [key: string]: unknown;
  };
  images?: string[];
  commented?: { at: string };
  // Admin-confirmed: the imported review's original post is gone at its source. The card shows
  // a neutral note; the dead post link is already stripped server-side.
  sourceRemovedAt?: string;
  // The imported author's name was replaced server-side by a generic label (مشترية / مشتري).
  authorHidden?: boolean;
  // Set when an admin LINKED this imported review to a registered account. `user` is then that
  // account (with tier) and `isExternal` stays true, so the card shows a real author AND the
  // source badge. Absent on every unlinked review.
  assignedUser?: { at: string };
  // Author-edit disclosure: set server-side when the OWNER rewrote the review (never by an admin
  // correction, never bumped by other writes). Absent = never edited by its author.
  editedAt?: string;
  // Fake-campaign verdict from the backend detector. Flagged reviews are DOWNWEIGHTED, never
  // hidden: they are excluded from the store's rating but still render (badged, collapsed).
  // Absent on every review the detector has not scanned. Dates arrive as ISO strings.
  inauthentic?: {
    flagged: boolean;
    score: number;
    reasons: string[];
    burstWindowStart?: string;
    detectorVersion: number;
    flaggedAt: string;
    adminOverride?: { unflagged: boolean; at: string };
  };
  createdAt: string;
  updatedAt: string;
};

export type PerSourceStat = {
  source: string;
  count: number;
  averageRating: number;
};

export type ReviewStats = {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    [key: number]: number;
  };
  averageRatingFactors: RatingFactors;
  perSource?: PerSourceStat[];
  // Reviews with at least one image (server-side count, same exclusions as totalReviews).
  withImagesCount?: number;
  googleAggregate?: {
    rating: number;
    reviewCount: number;
  };
};

export type ReviewsResponse = {
  ok: boolean;
  data: {
    reviews: Review[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    stats: ReviewStats;
    // Total flagged reviews on the store, returned only when the caller passes `authenticity`.
    // Returned on BOTH tabs, so the «تقييمات مُستبعدة (N)» label survives a tab switch.
    flaggedCount?: number;
  };
};

export type CreateReviewRequest = {
  text?: string;
  overallRating: number;
  ratingFactors: RatingFactors;
  isAnonymous?: boolean;
};

export type CreateReviewResponse = {
  ok: boolean;
  data?: Review;
  error?: string;
};

export type ToggleLikeResponse = {
  ok: boolean;
  data?: {
    isLiked: boolean;
    likesCount: number;
  };
  error?: string;
};

export type AddCommentRequest = {
  text: string;
};

export type AddCommentResponse = {
  ok: boolean;
  data?: {
    comment: ReviewComment;
    commentsCount: number;
  };
  error?: string;
};

export type RatingFactorsResponse = {
  ok: boolean;
  data?: string[];
  error?: string;
};

// Rating factor labels in Arabic
export const ratingFactorLabels: { [key: string]: string } = {
  quality: "جودة المنتج/الخدمة",
  service: "خدمة العملاء",
  price: "السعر/القيمة مقابل المال",
  delivery: "التوصيل",
  atmosphere: "الأجواء",
  cleanliness: "النظافة",
  variety: "التنوع",
  location: "الموقع",
  packaging: "التغليف",
  experience: "التجربة العامة",
};

// One row of a review's activity trail (admin only). Mirrors backend ReviewActivityRow.
export type ReviewActivityRow = {
  id: string;
  action:
    | "created"
    | "imported"
    | "edited"
    | "images_added"
    | "deleted"
    | "soft_deleted"
    | "restored"
    | "store_reassigned"
    | "user_linked"
    | "user_unlinked"
    | "flagged"
    | "unflagged"
    | "purchase_refunded"
    | "provenance_updated";
  actor: { kind: "user" | "admin" | "moderator" | "system"; userId?: string; label?: string; name?: string };
  storeId?: string;
  changes?: Array<{ field: string; before?: unknown; after?: unknown }>;
  meta?: Record<string, unknown>;
  createdAt: string;
};
